/* PURE v2 hero PDP gallery (sections/pv-product-hero.liquid).
   Mobile (< 990px): the slides are a scroll-snap carousel; thumbnails scroll to a slide and follow the swipe.
   Desktop: slides are a grid (first = large). On a variant change (Dawn PUB_SUB variantChange) the
   variant's featured media moves to the first slot so shoppers see the lids they picked. */
if (!customElements.get('pv-ph-gallery')) {
  customElements.define(
    'pv-ph-gallery',
    class PvPhGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.pv-ph__slides');
        this.thumbs = Array.from(this.querySelectorAll('[data-go]'));
        if (!this.track) return;
        const selected = this.closest('product-info')?.querySelector('[data-selected-variant]');
        try {
          const media = JSON.parse(selected?.textContent || '{}').featured_media;
          if (media?.id) requestAnimationFrame(() => this.showMedia(media.id));
        } catch (_) {}

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.go(parseInt(thumb.dataset.go, 10)));
        });

        if ('IntersectionObserver' in window && this.thumbs.length) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                  this.mark(parseInt(entry.target.dataset.index, 10));
                }
              });
            },
            { root: this.track, threshold: [0.6] }
          );
          this.track.querySelectorAll('[data-index]').forEach((slide) => this.observer.observe(slide));
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data;
            if (!data || data.sectionId !== this.dataset.section) return;
            const media = data.variant && data.variant.featured_media;
            if (media && media.id) this.showMedia(media.id);
          });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.unsubscribe) this.unsubscribe();
      }

      isCarousel() {
        return window.matchMedia('(max-width: 989px)').matches;
      }

      slides() {
        return Array.from(this.track.querySelectorAll('[data-index]'));
      }

      go(index) {
        const slide = this.slides().find((s) => parseInt(s.dataset.index, 10) === index);
        if (!slide) return;
        if (this.isCarousel()) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
        } else {
          if (slide.dataset.mediaId) this.showMedia(slide.dataset.mediaId);
          slide.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.mark(index);
      }

      mark(index) {
        this.thumbs.forEach((t) => {
          const active = parseInt(t.dataset.go, 10) === index;
          t.classList.toggle('is-active', active);
          if (active) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }

      showMedia(mediaId) {
        const slide = this.track.querySelector(`[data-media-id="${mediaId}"]`);
        if (!slide) return;
        if (this.isCarousel()) {
          this.go(parseInt(slide.dataset.index, 10));
          return;
        }
        const first = this.track.firstElementChild;
        if (first === slide) return;
        first.classList.remove('pv-ph__slide--main');
        slide.classList.add('pv-ph__slide--main');
        this.track.insertBefore(slide, first);
        this.mark(parseInt(slide.dataset.index, 10));
      }
    }
  );
}

customElements.whenDefined('product-info').then(() => {
  document.querySelectorAll('product-info.pv-ph').forEach((info) => {
    if (info.dataset.heroRoutingReady) return;
    info.dataset.heroRoutingReady = 'true';
    const build = info.buildRequestUrlWithParams.bind(info);
    info.buildRequestUrlWithParams = (url, values, fullPage) => {
      const result = new URL(build(url, values, fullPage), window.location.origin);
      result.searchParams.set('view', 'hero');
      return result.pathname + result.search;
    };
    info.updateURL = (url, variantId) => {
      if (info.dataset.updateUrl === 'false') return;
      const result = new URL(url, window.location.origin);
      const current = new URL(window.location.href);
      current.searchParams.forEach((value, key) => {
        if (!['variant', 'option_values', 'section_id'].includes(key)) result.searchParams.set(key, value);
      });
      result.searchParams.set('view', 'hero');
      if (variantId) result.searchParams.set('variant', variantId);
      else result.searchParams.delete('variant');
      window.history.replaceState({}, '', result.pathname + result.search + current.hash);
    };
  });
});
